import React, { useContext, useEffect } from 'react'
import { StoreDispatch } from '../Store'
import { graphql } from 'gatsby'
import { animation } from '../styles/vars/animation.style'
import initTealiumTracker from 'tealium-tracker'
import { teliumDefaults } from '../tracking/telium'

// Components
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import FullWidthBanner from '../components/FullWidthBanner'
import ItinerarySection from '../components/ItinerarySection'
import Spacer from '../components/Spacer'

// Function
import { scrollToId } from '../utils/utils'

const ItineraryPage = ({ data, location }) => {
  const {
    pageContent,
    seoTitle,
    seoDescription,
    seoImage,
  } = data.contentfulPageItinerary

  const dispatch = useContext(StoreDispatch)

  useEffect(() => {
    const query = location.search

    const timeout = setTimeout(() => {
      if (query.length) {
        const id = query.substring(1).split('&')[0]
        scrollToId(id, dispatch, location)
      }
    }, (animation.maskShowDuration + animation.maskHideDuration) * 1000)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [location, dispatch])

  useEffect(() => {
    const { trackPageLoad } = initTealiumTracker()

    trackPageLoad({
      pageName: 'ultimatecruise:itinerary',
      ...teliumDefaults,
    })
  }, [])

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage.file.url}
        pathname={`/itinerary`}
      />

      {pageContent.map((section, key) => {
        if (section.__typename === `ContentfulSectionFullWidthBanner`) {
          return (
            <React.Fragment key={key}>
              <FullWidthBanner
                id={section.contentful_id}
                label={section.date}
                labelColor={section.dateColour}
                title={section.title}
                text={section.copy}
                image={section.backgroundImage}
                hasHeadingOne={key === 0 ? true : false}
                smallImage={section.smallImage}
                linkId={section.sectionId}
              />
              <Spacer size={[100, 250]} />
            </React.Fragment>
          )
        }

        if (section.__typename === `ContentfulSectionItineraryMonth`) {
          return (
            <ItinerarySection
              id={key}
              key={key}
              month={section.month}
              year={section.year}
              data={section.itineraryItems}
              pdf={section.fullItineraryPdf}
              linkId={section.sectionId}
              translations={data.contentfulGlobalSettings}
            />
          )
        }
        return ``
      })}

      <Footer />
    </>
  )
}

export default ItineraryPage

export const pageQuery = graphql`
  query ItineraryPageQuery($locale: String, $format: String) {
    contentfulPageItinerary {
      contentful_id
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      pageContent {
        __typename
        ... on ContentfulSectionFullWidthBanner {
          contentful_id
          sectionId
          date
          dateColour
          contentful_id
          backgroundImage {
            title
            gatsbyImageData(width: 3840, height: 2040, quality: 35)
          }
          smallImage {
            gatsbyImageData(height: 300)
          }
          title {
            raw
          }
          copy {
            raw
          }
        }
        ... on ContentfulSectionItineraryMonth {
          sectionId
          id
          month
          year
          fullItineraryPdf {
            file {
              url
            }
          }
          itineraryItems {
            ... on ContentfulItemItineraryItem {
              contentful_id
              location
              wonderIcon {
                gatsbyImageData
              }
              date1(formatString: $format, locale: $locale)
              image {
                gatsbyImageData(width: 308, height: 218)
              }
              link {
                link
                label
              }
              internal {
                type
              }
            }
            ... on ContentfulItemItineraryItemCruisingDay {
              contentful_id
              dateOfCruisingDay(formatString: $format, locale: $locale)
              internal {
                type
              }
            }
          }
        }
      }
    }
    contentfulGlobalSettings {
      viewCruisePortInformation
      printFullItinerary
      cruisingDay
    }
  }
`
